import {
  BatchesEnhancedDesignTemplateData,
  enhancedDesignColumns,
  featuredActionSampleValue,
} from './BatchesEnhancedDesignCsv';

export interface BatchesCreateCsvStructure {
  columns: string[];
  templateName: string;
}

interface FieldValue {
  id: string;
  label: string;
  defaultValue: string | null;
  overrideFieldValue: boolean;
}

interface FieldsSection {
  [key: string]: FieldValue | FieldsSection;
}

export interface BatchesCreateTemplateData extends BatchesEnhancedDesignTemplateData {
  fields?: {
    [sectionKey: string]: FieldsSection;
  };
}

// Helper function to recursively extract all 'id' values from nested fields structure
const extractFieldIds = (fields: FieldsSection | undefined): string[] => {
  if (!fields || typeof fields !== 'object') return [];

  const ids: string[] = [];

  for (const key in fields) {
    const value = fields[key];

    // If the value has an 'id' property, it's a field object
    if (value && typeof value === 'object' && 'id' in value) {
      const fieldValue = value as FieldValue;
      if (fieldValue.id) {
        ids.push(fieldValue.id);
      }
    }
    // Otherwise, it's a nested section, so recurse
    else if (value && typeof value === 'object') {
      ids.push(...extractFieldIds(value as FieldsSection));
    }
  }

  return ids;
};

export const buildBatchesCreateCsvStructure = (
  templateData: BatchesCreateTemplateData,
  templateName: string
): BatchesCreateCsvStructure => {
  const defaultColumns = [
    'ext_id',
    'group_tags',
    'nfc_enabled',
    'nfc_source',
    'nfc_custom_value',
    'barcode_enabled',
    'barcode_type',
    'barcode_source',
    'barcode_value',
    'barcode_display_text',
    'expires_at',
    'override_background_color',
    'override_label_color',
    'override_text_color',
    'override_banner_image_asset_id',
    'override_logo_image_asset_id',
    'override_thumbnail_image_asset_id',
  ];

  // Extract all field IDs from the nested fields structure
  const fieldIds = extractFieldIds(templateData.fields);
  const dynamicFields = fieldIds.map((id) => `passfield_${id}`);

  return {
    columns: [
      ...defaultColumns,
      ...enhancedDesignColumns(templateData),
      ...dynamicFields,
    ],
    templateName,
  };
};

export const downloadBatchesCreateSampleCsv = (
  csvStructure: BatchesCreateCsvStructure | null
) => {
  if (!csvStructure) return;

  const sampleDataRow = csvStructure.columns.map((column) => {
    switch (column) {
      case 'ext_id':
        return 'PASS123';
      case 'group_tags':
        return '"VIP Campaign, Loyalty Program"';
      case 'nfc_enabled':
        return null;
      case 'nfc_source':
        return null;
      case 'nfc_custom_value':
        return null;
      case 'barcode_enabled':
        return 'true';
      case 'barcode_type':
        return 'qr';
      case 'barcode_source':
        return 'custom';
      case 'barcode_value':
        return '1234567890';
      case 'barcode_display_text':
        return 'false';
      case 'expires_at':
        return 'ENTER DATE (2025-01-01T10:30:00.000Z)';
      case 'override_background_color':
        return '#000000';
      case 'override_label_color':
        return '#FFFFFF';
      case 'override_text_color':
        return '#FFFFFF';
      case 'override_banner_image_asset_id':
        return 'ENTER UUID';
      case 'override_logo_image_asset_id':
        return 'ENTER UUID';
      case 'override_thumbnail_image_asset_id':
        return 'ENTER UUID';
      case 'override_artwork_image_asset_id':
        return 'ENTER UUID';
      default:
        return (
          featuredActionSampleValue(column) ??
          (column.startsWith('passfield_') ? 'Sample Value' : '')
        );
    }
  });

  const csvContent = [
    csvStructure.columns.join(','),
    sampleDataRow.join(','),
    Array(csvStructure.columns.length).fill('').join(','),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.setAttribute('hidden', '');
  a.setAttribute('href', url);
  a.setAttribute('download', `${csvStructure.templateName}_template.csv`);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};
